import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ScrollReveal from './ScrollReveal';
import Seo from './Seo';

function Company() {
    return (
        <main>
            <Seo
                title="Company — Qcoda | Governed AI Orchestration"
                description="Qcoda is the governed AI orchestration platform. Founded by Andrew Schwabe (ForMotiv, Saigon AI) to close the accountability gap in enterprise AI adoption."
                path="/company"
            />

            <section className="site-section-lg">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10} xl={9}>
                            <ScrollReveal animation="fade-in">
                                <span className="badge-ea mb-4 d-inline-block">Company</span>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <h1 className="hero-headline mb-4">
                                    Building the control plane for enterprise AI.
                                </h1>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="hero-sub mb-4">
                                    Qcoda exists because AI adoption is racing ahead of the oversight
                                    needed to defend it. Every function is deploying AI faster than
                                    leadership, compliance, and the board can keep up with. We are
                                    building the platform that closes that gap — governance,
                                    orchestration, and alignment as first-class primitives, not
                                    afterthoughts.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Our thesis</span>
                                <h2 className="section-heading mt-3 mb-4">
                                    AI oversight cannot be bolted on later.
                                </h2>
                                <p className="section-subheading mb-3">
                                    The organizations that will win with AI are the ones that can prove
                                    what it did, why it did it, and who signed off — not the ones that
                                    move fastest without a trail.
                                </p>
                                <p className="section-subheading mb-3">
                                    Regulators, boards, auditors, and clients are converging on the same
                                    question: <em>where is the evidence?</em> The chat window does not
                                    survive that question. A ledgered, orchestrated platform does.
                                </p>
                                <p className="section-subheading">
                                    Qcoda is that platform, across every function adopting AI at work.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            <section id="founder" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Founder</span>
                                <h2 className="section-heading mt-3 mb-4">
                                    Operators who have built this before.
                                </h2>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={10} lg={8}>
                            <ScrollReveal animation="scale-in">
                                <div className="glass-card">
                                    <Row className="g-4 align-items-center">
                                        <Col md={4} className="text-center">
                                            <a href="https://www.linkedin.com/in/aschwabe/" target="_blank" rel="noopener noreferrer">
                                                <img
                                                    src="/team/andrew_schwabe.jpg"
                                                    alt="Andrew Schwabe, Founder & CEO of Qcoda"
                                                    className="team-photo"
                                                />
                                            </a>
                                        </Col>
                                        <Col md={8}>
                                            <p className="team-name" style={{ marginBottom: '0.25rem' }}>Andrew Schwabe</p>
                                            <p className="team-title mb-3">Founder &amp; CEO</p>
                                            <p className="team-bio mb-3">
                                                Serial entrepreneur and AI researcher. Founded 10 tech
                                                companies, including{' '}
                                                <a
                                                    href="https://formotiv.com"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="infra-link"
                                                >
                                                    ForMotiv
                                                </a>{' '}
                                                (behavioral analytics) and{' '}
                                                <a
                                                    href="https://saigonai.com"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="infra-link"
                                                >
                                                    Saigon AI
                                                </a>
                                                . Started Qcoda after watching AI adoption
                                                outrun oversight in every function it touched —
                                                engineering, legal, operations, research, marketing.
                                            </p>
                                            <p style={{ fontSize: '0.9rem', margin: 0 }}>
                                                <a
                                                    href="https://www.linkedin.com/in/aschwabe/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'rgba(120,170,240,0.95)', textDecoration: 'none', fontWeight: 500 }}
                                                >
                                                    LinkedIn &rarr;
                                                </a>
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            <section id="partners" className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Integration partners</span>
                                <h2 className="section-heading mt-3 mb-4">
                                    Built with the ecosystem.
                                </h2>
                                <p className="section-subheading mb-5">
                                    Qcoda integrates with model providers, inference platforms, and
                                    delivery partners so governance follows AI wherever it runs.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="scale-in">
                                <a
                                    href="https://www.saigonai.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="partner-card"
                                >
                                    <img
                                        src="/partners/saigonai.png"
                                        alt="Saigon AI"
                                        className="partner-logo"
                                    />
                                    <p className="partner-name">Saigon AI</p>
                                    <p className="partner-desc">
                                        AI delivery partner — engineering, research, and applied
                                        model work for regulated teams.
                                    </p>
                                </a>
                            </ScrollReveal>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mt-5">
                        <Col lg={9} xl={8}>
                            <ScrollReveal animation="fade-up">
                                <span className="eyebrow-quiet">Programs</span>
                                <h3 className="section-heading mt-3 mb-4" style={{ fontSize: '1.6rem' }}>
                                    Supported by leading startup programs.
                                </h3>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="scale-in">
                                <a
                                    href="https://www.nvidia.com/en-us/startups/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="partner-card"
                                >
                                    <img
                                        src="/partners/nvidia-inception.svg"
                                        alt="NVIDIA Inception Program"
                                        className="partner-logo"
                                    />
                                    <p className="partner-name">NVIDIA Inception</p>
                                    <p className="partner-desc">
                                        Member of NVIDIA's program for AI startups — compute credits,
                                        technical enablement, and go-to-market support.
                                    </p>
                                </a>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="scale-in" delay={80}>
                                <a
                                    href="https://www.microsoft.com/en-us/startups"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="partner-card"
                                >
                                    <img
                                        src="/partners/microsoft-for-startups.svg"
                                        alt="Microsoft for Startups"
                                        className="partner-logo"
                                    />
                                    <p className="partner-name">Microsoft for Startups</p>
                                    <p className="partner-desc">
                                        Azure credits, Microsoft 365, and enterprise co-sell paths for
                                        regulated AI deployments.
                                    </p>
                                </a>
                            </ScrollReveal>
                        </Col>
                        <Col md={6} lg={4}>
                            <ScrollReveal animation="scale-in" delay={160}>
                                <a
                                    href="https://aws.amazon.com/startups/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="partner-card"
                                >
                                    <img
                                        src="/partners/aws-activate.svg"
                                        alt="AWS Activate"
                                        className="partner-logo"
                                    />
                                    <p className="partner-name">AWS Activate</p>
                                    <p className="partner-desc">
                                        AWS credits and architecture support for scaling governed
                                        inference and audit infrastructure.
                                    </p>
                                </a>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <ScrollReveal animation="scale-in">
                                <div className="glass-card-lg text-center">
                                    <h2 className="section-heading mb-3">Work with us</h2>
                                    <p className="section-subheading mb-4">
                                        Design partner program is open across our four solution areas.
                                        Investor conversations welcome.
                                    </p>
                                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                                        <Link to="/contact" className="btn-primary-cta">Talk to us</Link>
                                        <Link to="/solutions" className="btn-secondary-cta">See solutions</Link>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Company;
